import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  User as FirebaseUser
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User } from '../types';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  register: (email: string, password: string, userData: Partial<User>) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  refreshUserData: () => Promise<void>;
  clearAuthState: () => void;
  forceLogout: () => Promise<void>;
  checkAndClearFirebaseSession: () => Promise<boolean>;
  clearAllFirebaseSessions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Verificar sesión de Firebase Auth al inicializar
  useEffect(() => {
    const verifySession = async () => {
      console.log('🔍 Verificando sesión de Firebase Auth al inicializar...');
      const currentUser = auth.currentUser;

      if (currentUser) {
        console.log('✅ Usuario autenticado encontrado:', currentUser.email);
      } else {
        console.log('🚪 No hay usuario autenticado');
        setCurrentUser(null);
      }
    };

    verifySession();
  }, []);

  // Función para verificar sesiones de Firebase Auth
  const checkAndClearFirebaseSession = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log('✅ Sesión de Firebase Auth válida:', currentUser.email);
        return true;
      } else {
        console.log('🚪 No hay sesión de Firebase Auth');
        return false;
      }
    } catch (error) {
      console.error('❌ Error verificando sesión de Firebase Auth:', error);
      return false;
    }
  };

  // Función para obtener datos del usuario desde Firestore
  const fetchUserData = async (firebaseUser: FirebaseUser): Promise<User> => {
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

    if (userDoc.exists()) {
      const data = userDoc.data();
      
      // Verificar si el usuario está pendiente de aprobación
      if (data.status === 'pending_approval') {
        console.warn('⚠️ Usuario pendiente de aprobación');
        
        // Si hay un registro en progreso, no interferir
        if (sessionStorage.getItem('registration_in_progress') === 'true') {
          console.log('⏳ Registro en progreso, no interferir...');
          const pendingError = new Error('PENDING_APPROVAL');
          (pendingError as any).isPendingApproval = true;
          (pendingError as any).skipSignOut = true;
          throw pendingError;
        }
        
        // Cerrar sesión silenciosamente y lanzar error especial (será manejado por el caller)
        await signOut(auth);
        const pendingError = new Error('PENDING_APPROVAL');
        (pendingError as any).isPendingApproval = true;
        throw pendingError;
      }

      let profilePhotoUrl = data.profilePhotoUrl;

      // Si es instructor y no tiene foto de perfil, intentar sincronizar desde instructor
      if (data.role === 'instructor' && data.instructorId && !profilePhotoUrl) {
        try {
          const instructorDoc = await getDoc(doc(db, 'instructors', data.instructorId));
          if (instructorDoc.exists() && instructorDoc.data().avatar) {
            profilePhotoUrl = instructorDoc.data().avatar;
            // Sincronizar la foto en el usuario
            await updateDoc(doc(db, 'users', firebaseUser.uid), {
              profilePhotoUrl: profilePhotoUrl
            });
            console.log('📸 Foto de instructor sincronizada con usuario');
          }
        } catch (error) {
          console.warn('No se pudo sincronizar foto de instructor:', error);
        }
      }
      
      return {
        ...data,
        id: firebaseUser.uid,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        // Manejar los nuevos campos de fecha
        membershipStartDate: data.membershipStartDate?.toDate() || undefined,
        lastPaymentDate: data.lastPaymentDate?.toDate() || undefined,
        nextDueDate: data.nextDueDate?.toDate() || undefined,
        // Asegurar que membershipHistory sea un array
        membershipHistory: data.membershipHistory || [],
        // Incluir foto de perfil (ya sincronizada si era instructor)
        profilePhotoUrl: profilePhotoUrl || undefined,
        // Asegurar businessId
        businessId: data.businessId || ''
      } as User;
    } else {
      // Si no existe, crear usuario por defecto (esto no debería pasar con el nuevo flujo)
      console.warn('⚠️ Usuario autenticado pero sin documento en Firestore');
      throw new Error('Usuario no encontrado en la base de datos');
    }
  };

  // Función para verificar si el email ya está registrado
  const checkEmailExists = async (email: string): Promise<boolean> => {
    const q = query(collection(db, 'users'), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Registro de usuario (Legacy - ahora se usa CreateUser y CreateBusiness)
  const register = async (email: string, password: string, userData: Partial<User>) => {
    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Crear documento de usuario en Firestore
      const newUser: User = {
        id: firebaseUser.uid,
        businessId: userData.businessId || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: email,
        phone: userData.phone || '',
        role: userData.role || 'user',
        status: 'active',
        membershipType: userData.membershipType || 'monthly_fee',
        profilePhotoUrl: userData.profilePhotoUrl || undefined,
        attendanceStreak: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        membershipStartDate: undefined,
        lastPaymentDate: undefined,
        nextDueDate: undefined,
        membershipHistory: []
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
      setCurrentUser(newUser);
    } catch (error: any) {
      console.error('Error en registro:', error);
      if (error?.code === 'auth/email-already-in-use') {
        throw new Error('El email ya está registrado');
      }
      throw new Error(error.message || 'Error al registrar usuario');
    }
  };

  // Reintenta obtener los datos del usuario mientras el backend crea el documento
  const fetchUserDataWithRetry = async (firebaseUser: FirebaseUser, maxAttempts = 8, delayMs = 500): Promise<User> => {
    let lastError: any;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Si la sesión cambió (logout o distinto usuario), abortar silenciosamente
        if (!auth.currentUser || auth.currentUser.uid !== firebaseUser.uid) {
          throw new Error('cancelled_fetch_due_to_auth_change');
        }
        const data = await fetchUserData(firebaseUser);
        return data;
      } catch (error: any) {
        lastError = error;
        // Si el documento aún no existe, esperar y reintentar
        if (error?.message?.toLowerCase().includes('no encontrado') || error?.message?.toLowerCase().includes('not found')) {
          console.warn(`⚠️ Documento de usuario aún no disponible (intento ${attempt}/${maxAttempts})`);
          if (attempt < maxAttempts) {
            await sleep(delayMs);
            continue;
          }
        }
        break;
      }
    }
    throw lastError;
  };

  // Login de usuario
  const login = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      const userData = await fetchUserData(firebaseUser);
      setCurrentUser(userData);
    } catch (error: any) {
      console.error('Error en login:', error);
      // Manejar el caso de usuario pendiente de aprobación
      if (error.message === 'PENDING_APPROVAL') {
        throw new Error('Tu cuenta está pendiente de aprobación por el administrador. Te notificaremos cuando sea aprobada.');
      }
      throw new Error(error.message || 'Error al iniciar sesión');
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      sessionStorage.clear();
      console.log('🚪 Sesión cerrada');
    } catch (error: any) {
      console.error('Error en logout:', error);
      throw new Error('Error al cerrar sesión');
    }
  };

  // Función para limpiar completamente el estado
  const clearAuthState = () => {
    setCurrentUser(null);
    console.log('🧹 Estado de autenticación limpiado completamente');
  };

  // Función para forzar logout completo
  const forceLogout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      sessionStorage.clear();
      console.log('🚪 Logout forzado completado');
    } catch (error) {
      console.error('Error en logout forzado:', error);
    }
  };

  // Función para limpiar completamente todas las sesiones de Firebase
  const clearAllFirebaseSessions = async () => {
    try {
      console.log('🧹 Limpiando todas las sesiones de Firebase...');
      await signOut(auth);
      setCurrentUser(null);
      localStorage.clear();
      sessionStorage.clear();

      document.cookie.split(";").forEach(function (c) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      console.log('✅ Todas las sesiones de Firebase limpiadas completamente');
    } catch (error) {
      console.error('❌ Error limpiando sesiones de Firebase:', error);
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      console.error('Error en reset password:', error);
      throw new Error(error.message || 'Error al enviar email de recuperación');
    }
  };

  // Actualizar perfil de usuario
  const updateUserProfile = async (data: Partial<User>) => {
    if (!currentUser) throw new Error('No hay usuario autenticado');

    try {
      console.log('🔄 Actualizando perfil del usuario:', currentUser.id);

      const updatedData = {
        ...data,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'users', currentUser.id), updatedData);

      const newUserData = { ...currentUser, ...updatedData };
      setCurrentUser(newUserData);
      console.log('✅ Perfil actualizado');

    } catch (error: any) {
      console.error('❌ Error al actualizar perfil:', error);
      throw new Error('Error al actualizar perfil');
    }
  };

  // Refrescar datos del usuario
  const refreshUserData = async () => {
    if (!auth.currentUser) return;

    try {
      const userData = await fetchUserData(auth.currentUser);
      setCurrentUser(userData);
    } catch (error: any) {
      console.error('Error al refrescar datos:', error);
    }
  };

  // Listener para cambios de autenticación con actualizaciones en tiempo real
  useEffect(() => {
    console.log('🔍 Iniciando listener de autenticación...');
    
    let userDocUnsubscribe: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔄 Estado de autenticación cambiado:', firebaseUser ? 'Usuario presente' : 'Sin usuario');

      // Cancelar listener de documento anterior si existe
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
        userDocUnsubscribe = null;
      }

      try {
        if (firebaseUser) {
          console.log('👤 Usuario autenticado:', firebaseUser.email);

          if (!firebaseUser.email || !firebaseUser.uid) {
            console.log('⚠️ Usuario inválido detectado, limpiando estado...');
            setCurrentUser(null);
            setLoading(false);
            return;
          }

          // Primero cargar datos iniciales con reintento
          const initialData = await fetchUserDataWithRetry(firebaseUser);
          setCurrentUser(initialData);
          setLoading(false);
          
          // Luego crear listener en tiempo real para actualizaciones
          userDocUnsubscribe = onSnapshot(
            doc(db, 'users', firebaseUser.uid),
            (docSnapshot) => {
              if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                const updatedUser: User = {
                  ...data,
                  id: firebaseUser.uid,
                  createdAt: data.createdAt?.toDate() || new Date(),
                  updatedAt: data.updatedAt?.toDate() || new Date(),
                  membershipStartDate: data.membershipStartDate?.toDate() || undefined,
                  lastPaymentDate: data.lastPaymentDate?.toDate() || undefined,
                  nextDueDate: data.nextDueDate?.toDate() || undefined,
                  membershipHistory: data.membershipHistory || [],
                  profilePhotoUrl: data.profilePhotoUrl || undefined,
                } as User;
                console.log('🔄 Datos de usuario actualizados en tiempo real');
                setCurrentUser(updatedUser);
              }
            },
            (error) => {
              console.error('Error en listener de usuario:', error);
            }
          );
        } else {
          console.log('🚪 No hay usuario autenticado, limpiando estado...');
          setCurrentUser(null);
          sessionStorage.clear();
          setLoading(false);
        }
      } catch (error) {
        // Ignorar errores esperables
        const msg = (error as any)?.message || '';
        const code = (error as any)?.code || '';
        const isPendingApproval = (error as any)?.isPendingApproval || msg.includes('PENDING_APPROVAL');
        
        if (isPendingApproval) {
          // Usuario pendiente de aprobación - manejar silenciosamente
          console.log('⏳ Usuario pendiente de aprobación, sesión cerrada.');
        } else if (msg.includes('cancelled_fetch_due_to_auth_change') || (!auth.currentUser && code === 'permission-denied')) {
          console.log('ℹ️ Lectura de usuario cancelada tras cambio de sesión.');
        } else {
          console.error('❌ Error al obtener datos del usuario:', error);
        }
        setCurrentUser(null);
        sessionStorage.clear();
        setLoading(false);
      }
      console.log('✅ Listener de autenticación completado');
    });

    return () => {
      unsubscribeAuth();
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
      }
    };
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading,
    register,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    refreshUserData,
    clearAuthState,
    forceLogout,
    checkAndClearFirebaseSession,
    clearAllFirebaseSessions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 